import { Component } from 'react';

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';

import { PAGE_USERS } from 'shared/containers/GenericUsers';
import GenericUsers from 'shared/containers/GenericUsers/GenericUsers';
import GenericUsersManageData from 'shared/containers/GenericUsers/GenericUsersManageData/GenericUsersManageData';
import accessToken from 'utils/accessToken';
import { AuthState, useAuth } from 'utils/hooks/providers/auth-context';

import Dashboard, { PAGE_DASHBOARD } from './Dashboard';
import Flows, { PAGE_FLOWS } from './Flows';
import DeviceConfigDetails from './Flows/FlowsDetails/DeviceConfig/DeviceConfigDetails/DeviceConfigDetails';
import DeviceConfigCreate from './Flows/FlowsDetails/DeviceConfig/DeviceConfigManageData/DeviceConfigCreate';
import DeviceConfigEdit from './Flows/FlowsDetails/DeviceConfig/DeviceConfigManageData/DeviceConfigEdit';
import DeviceProfileDetails from './Flows/FlowsDetails/DeviceProfile/DeviceProfileDetails/DeviceProfileDetails';
import DeviceProfileManageData from './Flows/FlowsDetails/DeviceProfile/DeviceProfileManageData';
import FlowsDetails from './Flows/FlowsDetails/FlowsDetails';
import FlowsManageData from './Flows/FlowsManageData';
import InstanceSettings, { PAGE_INSTANCE_SETTINGS } from './InstanceSettings';
import IPNetworks, { PAGE_IP_NETWORKS } from './IPNetworks';
import IPNetworksDetails from './IPNetworks/IPNetworksDetails/IPNetworksDetails';
import IPNetworksManageData from './IPNetworks/IPNetworksManageData';
import Layout from './Layout';
import Login, { PAGE_LOGIN } from './Login';
import Organizations, { PAGE_ORGANISATIONS } from './Organizations';
import OrganizationsDetails from './Organizations/OrganizationsDetails/OrganizationsDetails';
import OrganizationsManageData from './Organizations/OrganizationsManageData/OrganizationsManageData';
import SCHCTemplates, { PAGE_SCHC_TEMPLATES } from './SCHCTemplates';
import SCHCTemplatesDetails from './SCHCTemplates/SCHCTemplatesDetails/SCHCTemplatesDetails';
import SCHCTemplatesManageData from './SCHCTemplates/SCHCTemplatesManageData';
import Search, { PAGE_SEARCH } from './Search';
import Sources, { PAGE_SOURCES } from './Sources';
import SourcesDetails from './Sources/SourcesDetails';
import SourcesManageData from './Sources/SourcesManageData/SourcesManageData';

export const PAGE_USER_PROFILE = '/me';

export const GetAvailableRoutes = (authStore: AuthState): Array<string> => {
  const isOrganization = !!authStore.organization;
  const isAdmin = authStore.admin;
  const isMultiOrg = authStore.multi;

  let result: Array<string> = [PAGE_USER_PROFILE, PAGE_SCHC_TEMPLATES, PAGE_SEARCH];
  if (!isOrganization) {
    result = result.concat([PAGE_DASHBOARD, PAGE_ORGANISATIONS]);
    if (isAdmin) {
      result = result.concat([PAGE_INSTANCE_SETTINGS]);
    }
  }
  if (isOrganization) {
    result = result.concat([PAGE_FLOWS, PAGE_SOURCES, PAGE_IP_NETWORKS]);
  }
  if ((isOrganization && isMultiOrg) || isAdmin) {
    result = result.concat([PAGE_USERS]);
  }
  return result;
};

export interface RouterParams {
  id?: string;
  flowId?: string;
  deviceProfileId?: string;
  deviceConfigId?: string;
  schcTemplateId?: string;
  username?: string;
  organizationName?: string;
}

type ProtectRouteProps = {
  component: Component | any,
  path: string,
  exact?: boolean,
  private?: boolean,
  children?: any,
}
/* eslint-disable */
const ProtectRoute = (props: ProtectRouteProps): any => {
  const routePrefix = '/' + props.path.split('/')
    .filter(i => i)[0]; // Remove empty boxes

  const isLogged: boolean = accessToken.hasToken();
  if (isLogged) {
    const { authState } = useAuth();
    const allowedPages = GetAvailableRoutes(authState);
    if (props.private) {
      if (allowedPages?.includes(routePrefix)) {
        return (
          <Route exact={props.exact} path={props.path}>
            <Layout>
              {props.component}
            </Layout>
          </Route>
        );
      }
    }
    const redirectTo = allowedPages?.includes(PAGE_FLOWS) ? PAGE_FLOWS : PAGE_DASHBOARD;
    return (<Redirect to={redirectTo} />);
  }
  if (!props.private) {
    return (
      <Route exact={props.exact} path={props.path} component={props.component}>
        {props.children}
      </Route>
    );
  }
  return (
    <Redirect
      to={{
        pathname: PAGE_LOGIN,
        state: {
          // Allows to get the path to come back after login
          referrer: window.location.pathname,
        }
      }}
    />
  );
};

// https://reactrouter.com/web/example/auth-workflow
// https://reactrouter.com/web/example/route-config

const AppRouter = () => (
  <Router>
    <Switch>
      <Route exact path={PAGE_LOGIN} component={Login} />
      <ProtectRoute private exact path={`${PAGE_SOURCES}`} component={Sources} />
      <ProtectRoute private exact path={`${PAGE_SOURCES}/create`} component={SourcesManageData} />
      <ProtectRoute private exact path={`${PAGE_SOURCES}/:id`} component={SourcesDetails} />
      <ProtectRoute private exact path={`${PAGE_SOURCES}/:id/edit`} component={SourcesManageData} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}`} component={Flows} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/create`} component={FlowsManageData} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId`} component={FlowsDetails} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/edit`} component={FlowsManageData} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/device-profiles`} component={FlowsDetails} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/device-profiles/create`} component={DeviceProfileManageData} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/device-profiles/:deviceProfileId`} component={DeviceProfileDetails} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/device-profiles/:deviceProfileId/edit`} component={DeviceProfileManageData} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/devices/create`} component={DeviceConfigCreate} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/device-profiles/:deviceProfileId/devices/:deviceConfigId`} component={DeviceConfigDetails} />
      <ProtectRoute private exact path={`${PAGE_FLOWS}/:flowId/device-profiles/:deviceProfileId/devices/:deviceConfigId/edit`} component={DeviceConfigEdit} />
      <ProtectRoute private exact path={`${PAGE_IP_NETWORKS}`} component={IPNetworks} />
      <ProtectRoute private exact path={`${PAGE_IP_NETWORKS}/create`} component={IPNetworksManageData} />
      <ProtectRoute private exact path={`${PAGE_IP_NETWORKS}/:id`} component={IPNetworksDetails} />
      <ProtectRoute private exact path={`${PAGE_IP_NETWORKS}/:id/edit`} component={IPNetworksManageData} />
      <ProtectRoute private exact path={`${PAGE_ORGANISATIONS}`} component={Organizations} />
      <ProtectRoute private exact path={`${PAGE_ORGANISATIONS}/create`} component={OrganizationsManageData} />
      <ProtectRoute private exact path={`${PAGE_ORGANISATIONS}/:organizationName`} component={OrganizationsDetails} />
      <ProtectRoute private exact path={`${PAGE_ORGANISATIONS}/:organizationName/edit`} component={OrganizationsManageData} />
      <ProtectRoute private exact path={`${PAGE_ORGANISATIONS}/:organizationName${PAGE_USERS}/create`} component={GenericUsersManageData} />
      <ProtectRoute private exact path={`${PAGE_ORGANISATIONS}/:organizationName${PAGE_USERS}/:username`} component={GenericUsersManageData} />
      <ProtectRoute private exact path={`${PAGE_SCHC_TEMPLATES}`} component={SCHCTemplates} />
      <ProtectRoute private exact path={`${PAGE_SCHC_TEMPLATES}/create`} component={SCHCTemplatesManageData} />
      <ProtectRoute private exact path={`${PAGE_SCHC_TEMPLATES}/:schcTemplateId`} component={SCHCTemplatesDetails} />
      <ProtectRoute private exact path={`${PAGE_SCHC_TEMPLATES}/:schcTemplateId/edit`} component={SCHCTemplatesManageData} />
      <ProtectRoute private exact path={`${PAGE_DASHBOARD}`} component={Dashboard} />
      <ProtectRoute private exact path={`${PAGE_INSTANCE_SETTINGS}`} component={InstanceSettings} />
      <ProtectRoute private exact path={`${PAGE_USERS}`} component={(<div><GenericUsers /></div>)} />
      <ProtectRoute private exact path={`${PAGE_USERS}/create`} component={GenericUsersManageData} />
      <ProtectRoute private exact path={`${PAGE_USERS}/:username`} component={GenericUsersManageData} />
      <ProtectRoute private exact path={`${PAGE_USER_PROFILE}`} component={GenericUsersManageData} />
      <ProtectRoute private exact path={`${PAGE_SEARCH}`} component={Search} />
      <Redirect to={PAGE_FLOWS} />
    </Switch>
  </Router>
);

export default AppRouter;
